import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaTimes, FaArrowLeft } from "react-icons/fa";
import { useSignatureStateManager } from "../../Signature/hooks/useSignatureStateManager";
import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
  getDoc,
  deleteField,
} from "firebase/firestore";
import {
  getOrCreatePageId,
  updateEmployeePage,
  checkIfPageCreationAllowed,
} from "../../../utils/employeePages";
import { getAssignmentUrl } from "../../../config/urls";
import { logActivity, ACTION_TYPES } from "../../../utils/historyLogger";
import { normalizeItemSearchText } from "../../../utils/itemSearchText";
import { sendAssignmentSms } from "../../../services/smsService";
import { useKits } from "../../../hooks/useKits";
import CartSection from "../../Signature/components/CartSection";
import ItemSearchInline from "../../Signature/components/ItemSearchInline";
import KitSection from "../../Signature/components/KitSection";
import SignatureModal from "../../Signature/components/SignatureModal";
import UrlModal from "../../Signature/components/UrlModal";
import "../../Signature/Signature.css";
import "./RefundModal.css";

const RefundModal = ({
  isOpen,
  onClose,
  inventoryItem,
  selectedEmployee,
  inventory,
  items,
  personnel,
  units,
  user,
  onRefresh,
}) => {
  const currentUser = useMemo(
    () => ({
      id: user?.email || user?.id || "",
      email: user?.email || "",
      permissions: user?.permissions || "childUser",
      employeeId: user?.employeeId || "",
    }),
    [user],
  );

  const isChildUser = useMemo(
    () => currentUser.permissions === "childUser",
    [currentUser.permissions],
  );

  const childUserPersonalNumber = useMemo(() => {
    if (!isChildUser) return null;
    return user?.employeeId || null;
  }, [isChildUser, user?.employeeId]);

  const { kits, addKit, updateKit, deleteKit, reorderKits } = useKits();

  const {
    showSignatureModal,
    openSignatureModal,
    closeSignatureModal,

    itemSearchTerm,
    selectedItem,
    showItemOptions,
    serialSearchTerm,
    selectedSerial,
    showSerialOptions,
    quantity,
    note,
    resetAddItemForm,
    handleItemSelect,
    handleSerialSelect,
    handleItemInputChange,
    handleSerialInputChange,
    handleQuantityChange,
    handleNoteChange,
    handleItemInputFocus,
    handleItemInputBlur,
    handleSerialInputFocus,
    handleSerialInputBlur,

    signaturePadRef,
    setSignaturePadRef,
    hasSignature,
    setHasSignature,
    signatureData,
    setSignatureData,
    clearMainSignature,
    getSignatureData,

    cart,
    setCart,
    editingQuantities,
    setEditingQuantities,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,

    resetAllState,
  } = useSignatureStateManager();

  const debounceTimersRef = useRef({});
  const activeInputRef = useRef({ id: null, position: null });
  const filterInputRef = useRef(null);
  const shouldMaintainFilterFocus = useRef(false);
  const smsSentRef = useRef(false);
  /** Last inventory row id we auto-seeded into the return cart — re-seed when switching rows while modal stays open */
  const cartSeededForItemIdRef = useRef(null);

  const [assignedItemsFilter, setAssignedItemsFilter] = useState("");
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlModalData, setUrlModalData] = useState({ url: "", message: "" });

  const getUnitName = useCallback(
    (unitId) => {
      if (!unitId || !units?.length) return "לא מוגדר";
      const unit = units.find((u) => u.id === unitId);
      return unit?.unitName || "לא מוגדר";
    },
    [units],
  );

  const assignedItems = useMemo(() => {
    if (!selectedEmployee || !inventory || !items) return [];

    return inventory
      .filter((item) => {
        if (isChildUser && currentUser.employeeId) {
          const firstAssignment =
            item.assignments?.first?.[currentUser.employeeId];
          const secondAssignment =
            item.assignments?.second?.[selectedEmployee.personalNumber];
          return (
            firstAssignment &&
            firstAssignment.quantity > 0 &&
            secondAssignment &&
            secondAssignment.quantity > 0
          );
        } else {
          const firstAssignment =
            item.assignments?.first?.[selectedEmployee.personalNumber];
          return firstAssignment && firstAssignment.quantity > 0;
        }
      })
      .map((item) => {
        let assignment;
        let assignedQuantity = 0;
        let assignedAt = null;

        if (isChildUser && currentUser.employeeId) {
          assignment =
            item.assignments?.second?.[selectedEmployee.personalNumber];
          assignedQuantity = assignment?.quantity || 0;
          assignedAt = assignment?.assignedAt;
        } else {
          assignment =
            item.assignments?.first?.[selectedEmployee.personalNumber];
          assignedQuantity = assignment?.quantity || 0;
          assignedAt = assignment?.assignedAt;
        }

        const itemDefinition = items.find(
          (i) => i.id === item.itemDefinitionId,
        );

        let availableQuantity = 0;
        if (isChildUser && currentUser.employeeId && !item.serialNumber) {
          const firstAssignment =
            item.assignments?.first?.[currentUser.employeeId];
          if (firstAssignment && firstAssignment.quantity > 0) {
            const firstQuantity = firstAssignment.quantity || 0;
            const secondAssignments = item.assignments?.second || {};
            const secondSum = Object.values(secondAssignments).reduce(
              (sum, a) => sum + (a.quantity || 0),
              0,
            );
            availableQuantity = Math.max(0, firstQuantity - secondSum);
          }
        } else if (!isChildUser && !item.serialNumber) {
          availableQuantity = item.availableQuantity ?? 0;
        }

        return {
          ...item,
          assignedQuantity,
          assignedAt,
          itemName: itemDefinition?.itemName || "Unknown Item",
          isSerialItem: !!item.serialNumber,
          availableQuantity,
        };
      });
  }, [selectedEmployee, inventory, items, isChildUser, currentUser]);

  const filteredAssignedItems = useMemo(() => {
    if (!assignedItemsFilter.trim()) return assignedItems;
    const filterTerm = normalizeItemSearchText(assignedItemsFilter).trim();
    return assignedItems.filter((item) => {
      const itemName = normalizeItemSearchText(item.itemName);
      const serialNumber = normalizeItemSearchText(item.serialNumber);
      return itemName.includes(filterTerm) || serialNumber.includes(filterTerm);
    });
  }, [assignedItems, assignedItemsFilter]);

  // Auto-add the clicked item to return cart on open (re-run when switching to another row while modal stays open)
  useEffect(() => {
    if (!isOpen || !inventoryItem || !selectedEmployee) return;

    const matchedItem = assignedItems.find((ai) => ai.id === inventoryItem.id);
    if (!matchedItem) return;

    if (cartSeededForItemIdRef.current === inventoryItem.id) return;
    cartSeededForItemIdRef.current = inventoryItem.id;

    const fullReturnQuantity = -Math.abs(matchedItem.assignedQuantity);
    const assignedQuantity = Math.abs(matchedItem.assignedQuantity);

    setEditingQuantities(() => ({
      [matchedItem.id]: 0,
    }));

    const returnCartItem = {
      id: matchedItem.id || Date.now().toString(),
      item: matchedItem.itemName,
      serial: matchedItem.serialNumber || "",
      quantity: fullReturnQuantity,
      itemDefinitionId: matchedItem.itemDefinitionId,
      hasSerialNumber: !!matchedItem.serialNumber,
      isReturn: true,
      isOrganic: matchedItem.isOrganic,
      originalQuantity: assignedQuantity,
    };

    setCart([returnCartItem]);
  }, [
    isOpen,
    inventoryItem,
    selectedEmployee,
    assignedItems,
    setCart,
    setEditingQuantities,
  ]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      cartSeededForItemIdRef.current = null;
      resetAllState();
      setAssignedItemsFilter("");
      setShowUrlModal(false);
      setUrlModalData({ url: "", message: "" });
      smsSentRef.current = false;
      Object.values(debounceTimersRef.current).forEach((timer) =>
        clearTimeout(timer),
      );
      debounceTimersRef.current = {};
    }
  }, [isOpen, resetAllState]);

  // Restore focus to filter input after re-render
  useEffect(() => {
    if (!filterInputRef.current || !shouldMaintainFilterFocus.current) return;
    const rafId = requestAnimationFrame(() => {
      if (
        filterInputRef.current &&
        shouldMaintainFilterFocus.current &&
        document.activeElement !== filterInputRef.current
      ) {
        filterInputRef.current.focus();
        const pos = filterInputRef.current.value.length;
        filterInputRef.current.setSelectionRange(pos, pos);
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [assignedItemsFilter]);

  // Restore focus to quantity input after re-render
  useEffect(() => {
    const activeId = activeInputRef.current?.id;
    if (!activeId) return;
    const rafId = requestAnimationFrame(() => {
      const inputEl = document.getElementById(
        `refund-quantity-${activeId}`,
      );
      if (inputEl && document.activeElement !== inputEl) {
        inputEl.focus();
        const pos = activeInputRef.current.position;
        if (pos != null && pos >= 0) {
          inputEl.setSelectionRange(pos, pos);
        }
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [editingQuantities]);

  const handleReturnQuantityChange = useCallback((itemId, newQuantity) => {
    setEditingQuantities((prev) => ({
      ...prev,
      [itemId]: newQuantity,
    }));
  }, []);

  const handleEditQuantity = useCallback(
    (item, overrideQuantity = null) => {
      const newQuantity =
        overrideQuantity !== null
          ? overrideQuantity
          : editingQuantities[item.id] || item.assignedQuantity;
      const originalQuantity = item.assignedQuantity;
      const difference = newQuantity - originalQuantity;

      if (difference > 0) {
        let availableQuantity;
        if (isChildUser && item.availableQuantity !== undefined) {
          availableQuantity = item.availableQuantity;
        } else {
          availableQuantity = item.availableQuantity ?? 0;
        }

        const maxQuantity = originalQuantity + availableQuantity;

        if (newQuantity > maxQuantity) {
          const message =
            isChildUser && item.availableQuantity !== undefined
              ? `הכמות המקסימלית היא ${maxQuantity} (${originalQuantity} מוקצית + ${availableQuantity} זמין)`
              : `אין מספיק פריטים זמינים במלאי. זמין: ${availableQuantity}, נדרש: ${difference}`;

          alert(message);
          setEditingQuantities((prev) => ({
            ...prev,
            [item.id]: maxQuantity,
          }));
          setCart((prev) => prev.filter((ci) => ci.id !== item.id));
          return;
        }
      }

      if (difference !== 0) {
        const existingCartItem = cart.find((ci) => ci.id === item.id);

        if (existingCartItem) {
          setCart((prev) =>
            prev.map((ci) =>
              ci.id === item.id
                ? {
                    ...ci,
                    quantity: difference,
                    item: item.itemName || "",
                    serial: item.serialNumber || "",
                    itemDefinitionId: item.itemDefinitionId || "",
                    hasSerialNumber: item.isSerialItem || false,
                    isOrganic: item.isOrganic ?? null,
                    originalQuantity: originalQuantity,
                    finalQuantity: newQuantity,
                    availableQuantity: item.availableQuantity ?? 0,
                  }
                : ci,
            ),
          );
        } else {
          const cartItem = {
            id: item.id || "",
            item: item.itemName || "",
            serial: item.serialNumber || "",
            quantity: difference,
            itemDefinitionId: item.itemDefinitionId || "",
            hasSerialNumber: item.isSerialItem || false,
            isOrganic: item.isOrganic ?? null,
            originalQuantity: originalQuantity,
            finalQuantity: newQuantity,
            availableQuantity: item.availableQuantity ?? 0,
          };
          setCart((prev) => [...prev, cartItem]);
        }
      } else {
        setCart((prev) => prev.filter((ci) => ci.id !== item.id));
      }
    },
    [editingQuantities, cart, setCart, setEditingQuantities],
  );

  const debouncedHandleEditQuantity = useCallback(
    (item, newQuantity) => {
      if (debounceTimersRef.current[item.id]) {
        clearTimeout(debounceTimersRef.current[item.id]);
      }
      debounceTimersRef.current[item.id] = setTimeout(() => {
        handleEditQuantity(item, newQuantity);
        delete debounceTimersRef.current[item.id];
      }, 800);
    },
    [handleEditQuantity],
  );

  const handleClearCart = useCallback(() => {
    clearCart();
    setEditingQuantities({});
  }, [clearCart]);

  const handleRemoveFromCart = useCallback(
    (itemId) => {
      removeFromCart(itemId);
      setEditingQuantities((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    },
    [removeFromCart],
  );

  const handleCartQuantityChange = useCallback(
    (itemId, newDifference) => {
      const cartItem = cart.find((ci) => ci.id === itemId);
      if (!cartItem) return;
      const originalQuantity = cartItem.originalQuantity || 0;
      const finalQuantity = originalQuantity + newDifference;

      if (newDifference < 0) {
        const returnQuantity = Math.abs(newDifference);
        if (returnQuantity > originalQuantity) {
          alert(
            `לא ניתן להחזיר יותר מהכמות המוקצית. מוקצית: ${originalQuantity}, מנסה להחזיר: ${returnQuantity}`,
          );
          return;
        }
      }

      updateCartQuantity(itemId, newDifference);
      setEditingQuantities((prev) => ({
        ...prev,
        [itemId]: finalQuantity,
      }));
    },
    [updateCartQuantity, cart],
  );

  const handleAddReturnItem = useCallback(
    (item) => {
      if (!selectedEmployee) return;

      const fullReturnQuantity = -Math.abs(item.assignedQuantity);
      const assignedQuantity = Math.abs(item.assignedQuantity);

      setEditingQuantities((prev) => ({
        ...prev,
        [item.id]: 0,
      }));

      const isSerialItem =
        !!item.serialNumber && item.serialNumber.trim() !== "";

      const existingCartItem = cart.find((cartItem) => {
        if (cartItem.id === item.id) return true;
        if (isSerialItem) return false;
        return (
          (cartItem.itemDefinitionId === item.itemDefinitionId ||
            cartItem.id === item.id) &&
          cartItem.isOrganic === item.isOrganic &&
          (!cartItem.serial || cartItem.serial === "")
        );
      });

      if (existingCartItem) {
        setCart((prevCart) =>
          prevCart.map((cartItem) => {
            let isMatch = false;
            if (cartItem.id === item.id) {
              isMatch = true;
            } else if (!isSerialItem) {
              isMatch =
                (cartItem.itemDefinitionId === item.itemDefinitionId ||
                  cartItem.id === item.id) &&
                cartItem.isOrganic === item.isOrganic &&
                (!cartItem.serial || cartItem.serial === "");
            }
            return isMatch
              ? {
                  ...cartItem,
                  quantity: fullReturnQuantity,
                  isReturn: true,
                  originalQuantity: assignedQuantity,
                  itemDefinitionId:
                    item.itemDefinitionId || cartItem.itemDefinitionId,
                  isOrganic: item.isOrganic ?? cartItem.isOrganic ?? null,
                }
              : cartItem;
          }),
        );
      } else {
        const returnCartItem = {
          id: item.id || Date.now().toString(),
          item: item.itemName,
          serial: item.serialNumber || "",
          quantity: fullReturnQuantity,
          itemDefinitionId: item.itemDefinitionId,
          hasSerialNumber: !!item.serialNumber,
          isReturn: true,
          isOrganic: item.isOrganic,
          originalQuantity: assignedQuantity,
        };
        setCart((prevCart) => [...prevCart, returnCartItem]);
      }
    },
    [selectedEmployee, cart, setEditingQuantities, setCart],
  );

  const availableItemNames = useMemo(() => {
    if (!inventory?.length || !items?.length) return [];

    const itemMap = new Map();
    items.forEach((item) => itemMap.set(item.id, item));

    const uniqueItems = new Map();
    inventory.forEach((invItem) => {
      if (isChildUser && childUserPersonalNumber) {
        const firstAssignment =
          invItem.assignments?.first?.[childUserPersonalNumber];
        if (!firstAssignment || firstAssignment.quantity <= 0) return;

        const itemDef = itemMap.get(invItem.itemDefinitionId);
        if (!itemDef) return;

        let availableQuantity = 0;
        if (itemDef.isChecked) {
          const secondAssignment =
            invItem.assignments?.second?.[childUserPersonalNumber];
          if (!secondAssignment) availableQuantity = 1;
        } else {
          const firstQuantity = firstAssignment.quantity || 0;
          const secondAssignments = invItem.assignments?.second;
          let secondSum = 0;
          if (secondAssignments) {
            secondSum = Object.values(secondAssignments).reduce(
              (sum, a) => sum + (a.quantity || 0),
              0,
            );
          }
          availableQuantity = Math.max(0, firstQuantity - secondSum);
        }

        if (availableQuantity > 0) {
          const key = itemDef.isChecked
            ? itemDef.itemName
            : `${itemDef.itemName} (${invItem.isOrganic ? "אורגני" : "קווי"})`;

          if (!uniqueItems.has(key)) {
            uniqueItems.set(key, {
              itemName: itemDef.itemName,
              itemDefinitionId: itemDef.id,
              isChecked: itemDef.isChecked,
              availableQuantity,
              isOrganic: invItem.isOrganic,
            });
          } else {
            const existing = uniqueItems.get(key);
            uniqueItems.set(key, {
              ...existing,
              availableQuantity: existing.availableQuantity + availableQuantity,
            });
          }
        }
      } else {
        const itemDef = itemMap.get(invItem.itemDefinitionId);
        if (!itemDef) return;

        const key = itemDef.isChecked
          ? itemDef.itemName
          : `${itemDef.itemName} (${invItem.isOrganic ? "אורגני" : "קווי"})`;

        let availableQuantity = 0;
        if (invItem.serialNumber) {
          if (invItem.status === "available") availableQuantity = 1;
        } else {
          availableQuantity = invItem.availableQuantity ?? 0;
        }

        if (availableQuantity > 0 || invItem.serialNumber) {
          if (!uniqueItems.has(key)) {
            uniqueItems.set(key, {
              itemName: itemDef.itemName,
              itemDefinitionId: itemDef.id,
              isChecked: itemDef.isChecked,
              availableQuantity,
              isOrganic: invItem.isOrganic,
            });
          } else {
            const existing = uniqueItems.get(key);
            uniqueItems.set(key, {
              ...existing,
              availableQuantity: existing.availableQuantity + availableQuantity,
            });
          }
        }
      }
    });

    return Array.from(uniqueItems.values());
  }, [inventory, items, isChildUser, childUserPersonalNumber]);

  const getRemainingQuantity = useCallback(
    (itemDefinitionId, isOrganic = null) => {
      const itemDefinition = items.find((item) => item.id === itemDefinitionId);
      if (!itemDefinition || itemDefinition.isChecked) return null;

      if (isChildUser && childUserPersonalNumber) {
        let totalAvailable = 0;
        inventory.forEach((invItem) => {
          if (
            invItem.itemDefinitionId === itemDefinitionId &&
            (isOrganic === null || invItem.isOrganic === isOrganic)
          ) {
            const firstAssignment =
              invItem.assignments?.first?.[childUserPersonalNumber];
            if (firstAssignment && firstAssignment.quantity > 0) {
              const firstQuantity = firstAssignment.quantity || 0;
              const secondAssignments = invItem.assignments?.second;
              let secondSum = 0;
              if (secondAssignments) {
                secondSum = Object.values(secondAssignments).reduce(
                  (sum, a) => sum + (a.quantity || 0),
                  0,
                );
              }
              totalAvailable += Math.max(0, firstQuantity - secondSum);
            }
          }
        });
        const currentCartQuantity = cart
          .filter(
            (ci) =>
              ci.itemDefinitionId === itemDefinitionId &&
              (isOrganic === null || ci.isOrganic === isOrganic),
          )
          .reduce((total, ci) => total + Math.abs(ci.quantity), 0);
        return Math.max(0, totalAvailable - currentCartQuantity);
      }

      let totalAvailable = 0;
      inventory.forEach((invItem) => {
        if (
          invItem.itemDefinitionId === itemDefinitionId &&
          (isOrganic === null || invItem.isOrganic === isOrganic) &&
          !invItem.serialNumber
        ) {
          totalAvailable += invItem.availableQuantity ?? 0;
        }
      });
      const currentCartQuantity = cart
        .filter(
          (ci) =>
            ci.itemDefinitionId === itemDefinitionId &&
            (isOrganic === null || ci.isOrganic === isOrganic),
        )
        .reduce((total, ci) => total + Math.abs(ci.quantity), 0);
      return Math.max(0, totalAvailable - currentCartQuantity);
    },
    [cart, inventory, items, isChildUser, childUserPersonalNumber],
  );

  const handleAddItem = useCallback(
    (cartItem) => {
      if (!cartItem || !selectedEmployee) return;

      if (cartItem.serial) {
        const existingSerialItem = cart.find(
          (ci) => ci.serial === cartItem.serial,
        );
        if (existingSerialItem) {
          alert("מספר סידורי זה כבר קיים בעגלה");
          return;
        }
      } else {
        const existingNonSerialItem = cart.find(
          (ci) =>
            !ci.serial &&
            ci.itemDefinitionId === cartItem.itemDefinitionId &&
            ci.isOrganic === cartItem.isOrganic,
        );
        if (existingNonSerialItem) {
          setCart((prevCart) =>
            prevCart.map((ci) =>
              ci.id === existingNonSerialItem.id
                ? { ...ci, quantity: ci.quantity + cartItem.quantity }
                : ci,
            ),
          );
          return;
        }
      }

      setCart((prevCart) => [...prevCart, cartItem]);
    },
    [selectedEmployee, cart, setCart],
  );

  const handleFinalizeAssignment = useCallback(() => {
    if (!selectedEmployee || cart.length === 0) return;
    openSignatureModal();
  }, [selectedEmployee, cart, openSignatureModal]);

  const handleAssignItem = useCallback(
    async (batch, cartItem, employee, quantityToAssign) => {
      let inventoryItemId = cartItem.id;

      if (!cartItem.hasSerialNumber) {
        if (isChildUser && currentUser.employeeId) {
          const assignedItem = inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === cartItem.itemDefinitionId &&
              invItem.assignments?.first?.[currentUser.employeeId] &&
              (cartItem.isOrganic === null ||
                invItem.isOrganic === cartItem.isOrganic),
          );
          if (!assignedItem) {
            console.error("No assigned inventory item found for childUser");
            return;
          }
          inventoryItemId = assignedItem.id;
        } else {
          const availableItem = inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === cartItem.itemDefinitionId &&
              invItem.status === "available" &&
              (cartItem.isOrganic === null ||
                invItem.isOrganic === cartItem.isOrganic),
          );
          if (!availableItem) {
            console.error("No available inventory item found for assignment");
            return;
          }
          inventoryItemId = availableItem.id;
        }
      }

      const inventoryItemRef = doc(db, "inventory", inventoryItemId);
      const currentDoc = await getDoc(inventoryItemRef);
      const currentItemData = currentDoc.data();

      const originalQuantity = cartItem.originalQuantity || 0;
      const finalQuantity = originalQuantity + quantityToAssign;
      const currentItem =
        inventory.find((item) => item.id === inventoryItemId) || currentItemData;

      let updateData = { updatedAt: serverTimestamp() };

      if (isChildUser && currentUser.employeeId) {
        updateData[`assignments.second.${employee.personalNumber}`] = {
          quantity: finalQuantity,
          assignedAt: serverTimestamp(),
          firstName: employee.firstName || "",
          lastName: employee.lastName || "",
          assignedBy: currentUser.employeeId,
        };
      } else {
        updateData[`assignments.first.${employee.personalNumber}`] = {
          quantity: finalQuantity,
          assignedAt: serverTimestamp(),
          firstName: employee.firstName || "",
          lastName: employee.lastName || "",
          assignedBy: currentUser.employeeId || currentUser.email || "",
        };
      }

      if (!cartItem.hasSerialNumber && currentItem) {
        if (isChildUser) {
          // Status calculated by useInventoryCalculations
        } else {
          const currentAvailableQuantity =
            currentItem.availableQuantity ?? currentItem.quantity ?? 0;
          const newAvailableQuantity = Math.max(
            0,
            currentAvailableQuantity - quantityToAssign,
          );
          updateData.availableQuantity = newAvailableQuantity;
          updateData.status =
            newAvailableQuantity === 0 ? "assigned" : "available";
        }
      } else {
        if (!isChildUser) {
          updateData.status = "assigned";
        }
      }

      batch.update(inventoryItemRef, updateData);
    },
    [inventory, currentUser, isChildUser],
  );

  const handleReturnItem = useCallback(
    async (batch, cartItem, employee, quantityToReturn) => {
      let inventoryItemId = cartItem.id;

      if (!cartItem.hasSerialNumber) {
        let assignedItem;
        if (isChildUser && currentUser.employeeId) {
          assignedItem = inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === cartItem.itemDefinitionId &&
              invItem.assignments?.first?.[currentUser.employeeId] &&
              invItem.assignments?.second?.[employee.personalNumber] &&
              (cartItem.isOrganic === null ||
                invItem.isOrganic === cartItem.isOrganic),
          );
        } else {
          assignedItem = inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === cartItem.itemDefinitionId &&
              invItem.assignments?.first?.[employee.personalNumber] &&
              (cartItem.isOrganic === null ||
                invItem.isOrganic === cartItem.isOrganic),
          );
        }

        if (!assignedItem) {
          console.error("No assigned inventory item found for return");
          alert("שגיאה: לא נמצא פריט מוחתם להחזרה. נא לנסות שוב.");
          return;
        }

        inventoryItemId = assignedItem.id;
        const inventoryItemRef = doc(db, "inventory", inventoryItemId);
        const currentDoc = await getDoc(inventoryItemRef);
        const currentItemData = currentDoc.data();

        if (isChildUser && currentUser.employeeId) {
          const currentAssignedQuantity =
            currentItemData?.assignments?.second?.[employee.personalNumber]
              ?.quantity ||
            assignedItem.assignments?.second?.[employee.personalNumber]
              ?.quantity ||
            0;
          const newAssignedQuantity =
            currentAssignedQuantity - quantityToReturn;

          if (newAssignedQuantity <= 0) {
            batch.update(inventoryItemRef, {
              [`assignments.second.${employee.personalNumber}`]: deleteField(),
              updatedAt: serverTimestamp(),
            });
          } else {
            batch.update(inventoryItemRef, {
              [`assignments.second.${employee.personalNumber}.quantity`]:
                newAssignedQuantity,
              [`assignments.second.${employee.personalNumber}.assignedAt`]:
                serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          }
        } else {
          const currentAssignedQuantity =
            currentItemData?.assignments?.first?.[employee.personalNumber]
              ?.quantity ||
            assignedItem.assignments?.first?.[employee.personalNumber]
              ?.quantity ||
            0;
          const newAssignedQuantity =
            currentAssignedQuantity - quantityToReturn;
          const currentAvailableQuantity =
            currentItemData?.availableQuantity ??
            assignedItem.availableQuantity ??
            assignedItem.quantity ??
            0;
          const newAvailableQuantity =
            currentAvailableQuantity + quantityToReturn;

          if (newAssignedQuantity <= 0) {
            batch.update(inventoryItemRef, {
              [`assignments.first.${employee.personalNumber}`]: deleteField(),
              availableQuantity: newAvailableQuantity,
              status: "available",
              updatedAt: serverTimestamp(),
            });
          } else {
            batch.update(inventoryItemRef, {
              [`assignments.first.${employee.personalNumber}.quantity`]:
                newAssignedQuantity,
              [`assignments.first.${employee.personalNumber}.assignedAt`]:
                serverTimestamp(),
              availableQuantity: newAvailableQuantity,
              status: "available",
              updatedAt: serverTimestamp(),
            });
          }
        }
      } else {
        let assignedItem;
        if (isChildUser && currentUser.employeeId) {
          assignedItem = inventory.find(
            (invItem) =>
              invItem.id === inventoryItemId &&
              invItem.assignments?.first?.[currentUser.employeeId] &&
              invItem.assignments?.second?.[employee.personalNumber],
          );
          if (assignedItem) {
            const inventoryItemRef = doc(db, "inventory", inventoryItemId);
            batch.update(inventoryItemRef, {
              [`assignments.second.${employee.personalNumber}`]: deleteField(),
              updatedAt: serverTimestamp(),
            });
          } else {
            console.error("No assigned inventory item found for return");
            alert("שגיאה: לא נמצא פריט מוחתם להחזרה. נא לנסות שוב.");
          }
        } else {
          const inventoryItemRef = doc(db, "inventory", inventoryItemId);
          batch.update(inventoryItemRef, {
            [`assignments.first.${employee.personalNumber}`]: deleteField(),
            status: "available",
            updatedAt: serverTimestamp(),
          });
        }
      }
    },
    [inventory, isChildUser, currentUser],
  );

  const handleSignatureConfirm = useCallback(
    async (providedSignatureDataUrl = null) => {
      let signatureDataUrl = providedSignatureDataUrl;

      if (!signatureDataUrl && signaturePadRef) {
        signatureDataUrl = getSignatureData(signaturePadRef);
      }

      if (
        !signatureDataUrl ||
        !signatureDataUrl.trim() ||
        !signatureDataUrl.startsWith("data:image")
      ) {
        alert("נא להוסיף חתימה לאישור ההקצאה");
        return;
      }

      if (!hasSignature) {
        alert("נא להוסיף חתימה לאישור ההקצאה");
        return;
      }

      try {
        if (!currentUser.email) {
          alert("שגיאה: לא נמצא מידע משתמש");
          return;
        }

        if (!selectedEmployee || !selectedEmployee.personalNumber) {
          alert("שגיאה: לא נמצא מידע חייל נבחר");
          return;
        }

        if (!cart || cart.length === 0) {
          alert("שגיאה: אין פריטים להקצאה");
          return;
        }

        const batch = writeBatch(db);

        const cartAssignmentLines = cart.map((cartItem) => ({
          quantity: cartItem.quantity || 0,
          assignedAt: new Date(),
        }));
        const targetPn = selectedEmployee.personalNumber || "";
        const childSigning = isChildUser && currentUser.employeeId;

        const assignmentData = {
          personnelId: targetPn,
          personnelName: `${selectedEmployee.firstName || ""} ${
            selectedEmployee.lastName || ""
          }`.trim(),
          signature: signatureDataUrl || "",
          timestamp: serverTimestamp(),
          assignedAt: new Date(),
          assignments: childSigning
            ? {
                first: null,
                second: { [targetPn]: cartAssignmentLines },
              }
            : {
                first: { [targetPn]: cartAssignmentLines },
                second: null,
              },
          items: cart.map((cartItem) => ({
            itemId: cartItem.item?.itemDefinitionId || "",
            itemName: cartItem.item?.itemName || "",
            serial: cartItem.serial || "",
            quantity: cartItem.quantity || 0,
            isSerial: cartItem.item?.isChecked || false,
          })),
        };

        const assignmentRef = doc(collection(db, "assignments"));
        batch.set(assignmentRef, assignmentData);

        for (const cartItem of cart) {
          const difference = cartItem.quantity || 0;

          if (difference < 0) {
            await handleReturnItem(
              batch,
              cartItem,
              selectedEmployee,
              Math.abs(difference),
            );
          } else if (difference > 0) {
            await handleAssignItem(
              batch,
              cartItem,
              selectedEmployee,
              difference,
            );
          }
        }

        await batch.commit();

        for (const cartItem of cart) {
          const difference = cartItem.quantity || 0;
          if (difference === 0) continue;

          const itemName =
            cartItem.item?.itemName ||
            cartItem.item ||
            inventory.find((item) => item.id === cartItem.id)?.itemName ||
            "פריט ללא שם";
          const serialNumber =
            cartItem.serial ||
            inventory.find((item) => item.id === cartItem.id)?.serialNumber ||
            null;

          let inventoryItemId = cartItem.id;
          if (!cartItem.hasSerialNumber) {
            const foundItem = inventory.find((item) => {
              if (item.id === cartItem.id) return true;
              if (
                item.itemDefinitionId === cartItem.itemDefinitionId &&
                (cartItem.isOrganic === null ||
                  item.isOrganic === cartItem.isOrganic)
              ) {
                if (isChildUser && currentUser.employeeId) {
                  return item.assignments?.first?.[currentUser.employeeId];
                }
                return item.status === "available";
              }
              return false;
            });
            if (foundItem) inventoryItemId = foundItem.id;
          }

          const assignedToName = (() => {
            const name = [selectedEmployee.firstName, selectedEmployee.lastName]
              .filter(Boolean)
              .join(" ")
              .trim();
            if (name) return name;
            const pn = selectedEmployee.personalNumber;
            return pn ? `מס׳ אישי: ${pn}` : "";
          })();

          if (difference > 0) {
            await logActivity({
              actionType: ACTION_TYPES.ASSIGNED,
              itemId: inventoryItemId,
              itemName,
              serialNumber,
              details: {
                assignedTo: assignedToName,
                quantity: Math.abs(difference),
                personalNumber: selectedEmployee.personalNumber,
              },
            });
          } else if (difference < 0) {
            await logActivity({
              actionType: ACTION_TYPES.RETURNED,
              itemId: inventoryItemId,
              itemName,
              serialNumber,
              details: {
                returnedFrom: assignedToName,
                quantity: Math.abs(difference),
                personalNumber: selectedEmployee.personalNumber,
              },
            });
          }
        }

        const assignments = cart.filter((item) => (item.quantity || 0) > 0);
        const returns = cart.filter((item) => (item.quantity || 0) < 0);

        let message = `עסקה הושלמה! `;
        if (assignments.length > 0)
          message += `${assignments.length} פריטים הוקצו `;
        if (returns.length > 0)
          message += `${returns.length} פריטים זוכו `;
        message += `לחייל ${selectedEmployee.firstName} ${selectedEmployee.lastName}.`;

        try {
          const canCreate = await checkIfPageCreationAllowed();

          if (canCreate) {
            const deltaItems = cart
              .filter((item) => item.quantity !== 0)
              .map((cartItem) => ({
                itemName: cartItem.item?.itemName || cartItem.item || "",
                quantity: cartItem.quantity || 0,
                serial: cartItem.serial || "",
                isSerial: cartItem.hasSerialNumber || false,
                itemDefinitionId: cartItem.itemDefinitionId || "",
                inventoryItemId: cartItem.id || "",
                action: cartItem.quantity > 0 ? "added" : "removed",
              }));

            if (deltaItems.length > 0) {
              let signedBy = "";
              let signedByName = "";

              if (isChildUser && currentUser.employeeId) {
                signedBy = currentUser.employeeId;
                const currentPersonnel = personnel.find(
                  (p) => p.personalNumber === signedBy,
                );
                if (currentPersonnel) {
                  signedByName =
                    `${currentPersonnel.firstName} ${currentPersonnel.lastName}`.trim();
                } else {
                  const fallbackPersonnel = personnel.find(
                    (p) => p.email === currentUser.email,
                  );
                  if (fallbackPersonnel) {
                    signedBy = fallbackPersonnel.personalNumber;
                    signedByName =
                      `${fallbackPersonnel.firstName} ${fallbackPersonnel.lastName}`.trim();
                  } else {
                    signedBy = currentUser.employeeId;
                    signedByName = "";
                  }
                }
              } else {
                let currentPersonnel = personnel.find(
                  (p) => p.email === currentUser.email,
                );
                if (!currentPersonnel && currentUser.employeeId) {
                  currentPersonnel = personnel.find(
                    (p) => p.personalNumber === currentUser.employeeId,
                  );
                }
                if (currentPersonnel) {
                  signedBy = currentPersonnel.personalNumber;
                  signedByName =
                    `${currentPersonnel.firstName} ${currentPersonnel.lastName}`.trim();
                } else {
                  signedBy = currentUser.email || "";
                  signedByName = "";
                }
              }

              if (!signedBy) {
                signedBy = currentUser.email || currentUser.employeeId || "";
              }

              const pageId = await getOrCreatePageId(
                selectedEmployee.id,
                selectedEmployee.personalNumber,
                currentUser.email,
              );

              const lastTransaction = {
                transactionId: assignmentRef.id,
                signedBy,
                signedByName,
                signedAt: serverTimestamp(),
                signatureDataUrl,
                items: deltaItems,
                createdAt: serverTimestamp(),
              };

              await updateEmployeePage(pageId, lastTransaction);

              const pageUrl = getAssignmentUrl(pageId);

              setUrlModalData({ url: pageUrl, message });
              setShowUrlModal(true);

              if (selectedEmployee.phone && !smsSentRef.current) {
                smsSentRef.current = true;
                sendAssignmentSms(
                  selectedEmployee.phone,
                  `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
                  pageUrl,
                  assignments.length > 0,
                  returns.length > 0,
                ).catch((err) => console.warn("[SMS] send failed:", err));
              }
            } else {
              alert(message);
            }
          } else {
            alert(message);
          }
        } catch (error) {
          console.error("Error updating employee page:", error);
          alert(message);
        }

        setCart([]);
        closeSignatureModal();
        setSignatureData("");
        setHasSignature(false);
        clearMainSignature();
        smsSentRef.current = false;

        if (!showUrlModal) {
          onClose();
          if (onRefresh) onRefresh();
        }
      } catch (error) {
        console.error("Error during assignment:", error);
        alert(
          `שגיאה בהקצאת הפריטים: ${
            error.message || "שגיאה לא ידועה"
          }. נא לנסות שוב.`,
        );
      }
    },
    [
      selectedEmployee,
      cart,
      signatureData,
      inventory,
      hasSignature,
      signaturePadRef,
      getSignatureData,
      setSignatureData,
      setHasSignature,
      clearMainSignature,
      handleReturnItem,
      handleAssignItem,
      isChildUser,
      currentUser,
      personnel,
      closeSignatureModal,
      setCart,
      onClose,
      onRefresh,
      showUrlModal,
    ],
  );

  const handleUrlModalClose = useCallback(() => {
    setShowUrlModal(false);
    onClose();
    if (onRefresh) onRefresh();
  }, [onClose, onRefresh]);

  if (!isOpen || !inventoryItem || !selectedEmployee) return null;

  return (
    <div id="refund-modal-scope">
      <div className="refund-modal-overlay" onClick={onClose}>
        <div
          className="refund-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="refund-modal-header">
            <h3>זיכוי פריטים</h3>
            <button className="refund-modal-close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <div className="refund-modal-body signature-page-scope">
            {/* Employee Details */}
            <div className="employee-details-section">
              <div className="employee-details-header">
                <h3>פרטי החייל</h3>
              </div>
              <div className="employee-details-content">
                <div className="detail-row">
                  <span className="detail-label">שם:</span>
                  <span className="detail-value">
                    {selectedEmployee.firstName} {selectedEmployee.lastName}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">מספר אישי:</span>
                  <span className="detail-value">
                    {selectedEmployee.personalNumber}
                  </span>
                </div>
                {selectedEmployee.unitId && (
                  <div className="detail-row">
                    <span className="detail-label">יחידה:</span>
                    <span className="detail-value">
                      {getUnitName(selectedEmployee.unitId)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Inline item search */}
            <ItemSearchInline
              inventory={inventory}
              items={items}
              cart={cart}
              getRemainingQuantity={getRemainingQuantity}
              onAddItem={handleAddItem}
              isChildUser={isChildUser}
              childUserPersonalNumber={childUserPersonalNumber}
            />
            <KitSection
              kits={kits}
              items={items}
              inventory={inventory}
              cart={cart}
              onAddItem={handleAddItem}
              getRemainingQuantity={getRemainingQuantity}
              isChildUser={isChildUser}
              childUserPersonalNumber={childUserPersonalNumber}
              canManageKits={!isChildUser}
              onSaveKit={async (name, kitItems, kitId) => {
                if (kitId) await updateKit(kitId, name, kitItems);
                else await addKit(name, kitItems, currentUser.id);
              }}
              onDeleteKit={deleteKit}
              onReorderKits={reorderKits}
            />

            {/* Cart Section */}
            <CartSection
              cart={cart}
              onRemoveFromCart={handleRemoveFromCart}
              onCartQuantityChange={handleCartQuantityChange}
              onClearCart={handleClearCart}
              onFinalizeAssignment={handleFinalizeAssignment}
            />

            {/* Assigned Items */}
            {assignedItems && assignedItems.length > 0 && (
              <div className="assigned-items-section">
                <div className="assigned-items-header">
                  <h4>פריטים חתומים</h4>
                  <input
                    ref={filterInputRef}
                    type="text"
                    className="assigned-items-filter"
                    placeholder="סנן לפי שם פריט או מספר סידורי..."
                    value={assignedItemsFilter}
                    onChange={(e) => setAssignedItemsFilter(e.target.value)}
                    onFocus={() => {
                      shouldMaintainFilterFocus.current = true;
                    }}
                    onBlur={() => {
                      shouldMaintainFilterFocus.current = false;
                    }}
                  />
                </div>
                <div className="assigned-items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>שם הפריט</th>
                        <th>מספר סידורי</th>
                        <th>כמות מוקצית</th>
                        <th>תאריך הקצאה</th>
                        <th>סטטוס</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAssignedItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="item-name-container">
                              <span className="item-name">
                                {item.itemName}
                              </span>
                              {item.isOrganic !== null && (
                                <span
                                  className={`organic-badge ${
                                    item.isOrganic ? "organic" : "synthetic"
                                  }`}
                                >
                                  {item.isOrganic ? "אורגני" : "קווי"}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{item.serialNumber || "-"}</td>
                          <td>
                            <div className="assigned-quantity-display">
                              <span className="quantity-value">
                                {item.isSerialItem &&
                                  Math.abs(item.assignedQuantity)}
                              </span>
                            </div>
                            {!item.isSerialItem && (
                              <div className="quantity-editor">
                                <input
                                  type="number"
                                  min="0"
                                  max={
                                    !item.isSerialItem &&
                                    item.availableQuantity !== undefined
                                      ? Math.abs(item.assignedQuantity) +
                                        item.availableQuantity
                                      : Math.abs(item.assignedQuantity)
                                  }
                                  id={`refund-quantity-${item.id}`}
                                  name={`refund-quantity-${item.id}`}
                                  value={
                                    editingQuantities[item.id] !== undefined
                                      ? editingQuantities[item.id]
                                      : Math.abs(item.assignedQuantity)
                                  }
                                  onChange={(e) => {
                                    const input = e.target;
                                    let newQuantity =
                                      input.value === ""
                                        ? Math.abs(item.assignedQuantity)
                                        : parseInt(input.value) || 0;

                                    const maxQuantity =
                                      !item.isSerialItem &&
                                      item.availableQuantity !== undefined
                                        ? Math.abs(item.assignedQuantity) +
                                          item.availableQuantity
                                        : Math.abs(item.assignedQuantity);

                                    if (newQuantity > maxQuantity) {
                                      newQuantity = maxQuantity;
                                      input.value = maxQuantity;
                                      if (
                                        !item.isSerialItem &&
                                        item.availableQuantity !== undefined
                                      ) {
                                        alert(
                                          `הכמות המקסימלית היא ${maxQuantity} (${Math.abs(
                                            item.assignedQuantity,
                                          )} מוקצית + ${
                                            item.availableQuantity
                                          } זמין)`,
                                        );
                                      } else {
                                        alert(
                                          `הכמות המקסימלית היא ${maxQuantity}`,
                                        );
                                      }
                                    }

                                    if (newQuantity < 0) {
                                      newQuantity = 0;
                                      input.value = 0;
                                    }

                                    if (document.activeElement === input) {
                                      activeInputRef.current = {
                                        id: item.id,
                                        position: input.selectionStart,
                                      };
                                    }

                                    handleReturnQuantityChange(
                                      item.id,
                                      newQuantity,
                                    );
                                    debouncedHandleEditQuantity(
                                      item,
                                      newQuantity,
                                    );
                                  }}
                                  onFocus={(e) => {
                                    const wasRestoring =
                                      activeInputRef.current?.id === item.id;
                                    if (!wasRestoring) {
                                      e.target.select();
                                    }
                                    activeInputRef.current = {
                                      id: item.id,
                                      position: null,
                                    };
                                  }}
                                  onBlur={() => {
                                    if (
                                      activeInputRef.current?.id === item.id
                                    ) {
                                      activeInputRef.current = {
                                        id: null,
                                        position: null,
                                      };
                                    }
                                  }}
                                  className="quantity-input"
                                />
                              </div>
                            )}
                          </td>
                          <td>
                            {item.assignedAt
                              ? item.assignedAt.toDate
                                ? item.assignedAt
                                    .toDate()
                                    .toLocaleDateString("he-IL")
                                : typeof item.assignedAt === "object" &&
                                    item.assignedAt.seconds
                                  ? new Date(
                                      item.assignedAt.seconds * 1000,
                                    ).toLocaleDateString("he-IL")
                                  : "-"
                              : "-"}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${item.status}`}
                            >
                              {item.status === "assigned"
                                ? "מוחתם"
                                : item.status === "available"
                                  ? "זמין"
                                  : item.status === "returned"
                                    ? "זוכה"
                                    : item.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="return-btn"
                              onClick={() => handleAddReturnItem(item)}
                              title="זיכוי"
                            >
                              <FaArrowLeft />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Modals */}
          <SignatureModal
            isOpen={showSignatureModal}
            onClose={closeSignatureModal}
            onConfirm={handleSignatureConfirm}
            selectedEmployee={selectedEmployee}
            cart={cart}
            signaturePadRef={signaturePadRef}
            setSignaturePadRef={setSignaturePadRef}
            hasSignature={hasSignature}
            setHasSignature={setHasSignature}
          />

          <UrlModal
            isOpen={showUrlModal}
            onClose={handleUrlModalClose}
            url={urlModalData.url}
            message={urlModalData.message}
          />
        </div>
      </div>
    </div>
  );
};

export default RefundModal;
